import {
  VuexModule,
  Module,
  Action,
  Mutation,
  getModule,
} from 'vuex-module-decorators';
import { login, logout, ILoginRequest, getGroup } from '@/api/users';
import {
  getUserToken,
  setUserToken,
  removeUserToken,
  getSupplierIds,
  setSupplierIds,
  removeSupplierIds,
} from '@/utils/localstorage';
import router, { resetRouter } from '@/router';
import { PermissionModule } from './permission';
import store from '@/store';
import { IUserTokenData } from '@/api/types';
import {
  convertUserTokenToRoles,
  convertUserTokenToExpiredTime,
  AppRoleType,
} from '@/utils/permission';

export interface IUserState {
  token: string;
  tokenValue: string;
  tokenExpiredTime: Date | null;
  name: string;
  roles: AppRoleType[];
  supplierIds: string[];
}

@Module({ dynamic: true, store, name: 'user' })
class User extends VuexModule implements IUserState {
  public token = getUserToken() || '';
  public tokenValue = this.token
    ? (JSON.parse(this.token) as IUserTokenData).value
    : '';
  public tokenExpiredTime: Date | null =
    this.token && (JSON.parse(this.token) as IUserTokenData).expiredTime
      ? convertUserTokenToExpiredTime(JSON.parse(this.token) as IUserTokenData)
      : null;
  public name = '';
  public roles: AppRoleType[] = [];
  public supplierIds: string[] = getSupplierIds()
    ? JSON.parse(getSupplierIds()!)
    : [];

  @Mutation
  private SET_TOKEN(token: string) {
    this.token = token;
  }

  @Mutation
  private SET_TOKEN_VALUE(tokenValue: string) {
    this.tokenValue = tokenValue;
  }

  @Mutation
  private SET_TOKEN_EXPIRE_TIME(tokenExpiredTime: Date | null) {
    this.tokenExpiredTime = tokenExpiredTime;
  }

  @Mutation
  private SET_NAME(name: string) {
    this.name = name;
  }

  @Mutation
  private SET_ROLES(roles: AppRoleType[]) {
    this.roles = roles;
  }

  @Mutation
  private SET_SUPPLIER_IDS(supplierIds: string[]) {
    this.supplierIds = supplierIds;
  }

  @Action
  public ResetToken() {
    removeUserToken();
    this.SET_TOKEN('');
    this.SET_TOKEN_VALUE('');
    this.SET_ROLES([]);
  }

  @Action({ rawError: true })
  public async Login(req: {
    supplierId: string;
    username: string;
    password: string;
  }) {
    try {
      const userToken = await login({
        supplierId: req.supplierId.trim(),
        username: req.username.trim(),
        password: req.password,
      } as ILoginRequest);
      const tokenStr = JSON.stringify(userToken);
      setUserToken(tokenStr);
      this.SET_TOKEN(tokenStr);
      this.SET_TOKEN_VALUE(userToken.value);
      this.SET_TOKEN_EXPIRE_TIME(convertUserTokenToExpiredTime(userToken));
    } catch (err) {
      throw err;
    }
  }

  @Action({ rawError: true })
  public async Logout() {
    if (this.token === '') {
      throw Error('Logout: token is undefined!');
    }
    try {
      await logout(JSON.parse(this.token) as IUserTokenData);
      removeUserToken();
      resetRouter();
      this.SET_TOKEN('');
      this.SET_TOKEN_VALUE('');
      this.SET_TOKEN_EXPIRE_TIME(null);
      this.SET_NAME('');
      this.SET_ROLES([]);
    } catch (err) {
      throw err;
    }
  }

  @Action
  public async GetUserInfo() {
    if (this.token === '') {
      throw Error('GetUserInfo: token is undefined!');
    }
    const userToken = JSON.parse(this.token) as IUserTokenData;
    if (!userToken) {
      throw Error('Verification failed, please Login again.');
    }
    const res = await getGroup({});
    const roles = convertUserTokenToRoles(userToken, res.group);
    // roles must be a non-empty array
    if (!roles || roles.length <= 0) {
      throw Error('GetUserInfo: roles must be a non-null array!');
    }
    this.SET_ROLES(roles);
    this.SET_NAME(userToken.displayName);
  }

  @Action
  public addSupplierId(supplierId: string) {
    if (this.supplierIds.indexOf(supplierId) < 0) {
      const supplierIds = JSON.parse(
        JSON.stringify(this.supplierIds)
      ) as string[];
      supplierIds.push(supplierId);
      setSupplierIds(JSON.stringify(supplierIds));
      this.SET_SUPPLIER_IDS(supplierIds);
    }
  }

  @Action
  public removeSupplierId(supplierId: string) {
    const index = this.supplierIds.indexOf(supplierId);
    if (index >= 0) {
      const supplierIds = [];
      for (let i = 0; i < this.supplierIds.length; i++) {
        if (i != index) {
          supplierIds.push(this.supplierIds[i]);
        }
      }
      setSupplierIds(JSON.stringify(supplierIds));
      this.SET_SUPPLIER_IDS(supplierIds);
    }
  }

  @Action
  public resetSupplierIds() {
    removeSupplierIds();
    this.SET_SUPPLIER_IDS([]);
  }
}

export const UserModule = getModule(User);
